/* ==========================================================================
   Crumpaw · Signature Steel Bowl
   Live preview + personalisation + sticky bar + social proof toast.

   Everything lives inside this IIFE. Nothing is attached to `window`; the only
   globally visible names are the `cpsb-*` custom element tags, which are
   namespaced so they cannot collide with theme scripts.

   Degradation contract: if anything in here throws, the buy form keeps
   working. Colour/size radios fall back to a plain <select name="id">, the
   personalisation radios carry their own `properties[...]` values, and the
   Add to cart button is never left disabled by a preview error.
   ========================================================================== */

(() => {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;

  /* ---------------------------------------------------------------- helpers */

  const clamp = (min, value, max) => Math.max(min, Math.min(max, value));

  /** Read and parse the JSON config script belonging to an element. */
  const readConfig = (root) => {
    const node = root.querySelector('[data-cpsb-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[cpsb] could not parse config', error);
      return null;
    }
  };

  /**
   * Minimal Shopify money formatter. Takes cents plus a money format string
   * such as "${{amount}}" and returns the formatted string. Falls back to a
   * plain dollar rendering if the format string is unusable.
   */
  const formatMoney = (cents, format) => {
    const amount = Number(cents) || 0;

    const withDelimiters = (value, precision, thousands, decimal) => {
      const fixed = (value / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    if (typeof format !== 'string' || !format.includes('{{')) {
      return `$${withDelimiters(amount, 2, ',', '.')}`;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, token) => {
      switch (token) {
        case 'amount':
          return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(amount, 2, "'", '.');
        default:
          return withDelimiters(amount, 2, ',', '.');
      }
    });
  };

  /** Fill {token} placeholders in an editor-provided string. */
  const interpolate = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
    );

  const titleCase = (value) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

  /* ------------------------------------------------------- state broadcast */

  const STATE_EVENT = 'cpsb:state';

  /**
   * What a tier takes off, in cents.
   *
   * `discountPct` is the percentage the tier advertises. The key is new on
   * purpose: a template still sending the old cents-denominated `discount` reads
   * as no discount here rather than as a wildly wrong percentage, so a
   * half-deployed theme shows the full price instead of a fictional one.
   *
   * Per unit, floored, then multiplied out - because that is what Shopify does
   * to a percentage discount, and the page has to land on the same cent as the
   * cart. Measured against the live store: 10% of a $49.99 bowl is 499c, not
   * 500c, so two bowls come to $90.00 rather than the $89.98 that rounding the
   * pair as a whole would print. Three at 15% is 749c each and $127.50.
   */
  const tierOff = (unit, tier) => {
    const pct = Number(tier && tier.discountPct) || 0;
    if (pct <= 0) return 0;
    const perUnit = Math.floor((unit * pct) / 100);
    return perUnit * (Number(tier.quantity) || 1);
  };

  /* =========================================================================
     <cpsb-personalizer>
     ========================================================================= */

  class CpsbPersonalizer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cpsbReady === 'true') return;

      this.config = readConfig(this);
      if (!this.config) return;

      this.dataset.cpsbReady = 'true';

      this.state = {
        colour: this.config.initialColour || 0,
        size: this.config.initialSize || 0,
        font: this.config.initialFont || 0,
        design: this.config.initialDesign || 0,
        ink: null, // null = inherit the colourway default
        tier: this.config.initialTier || 0,
        name: '',
      };

      this.#collectRefs();
      this.#bind();

      // Never trust the server-rendered disabled state to be correct for JS
      // users; recompute it from the current (empty) name.
      this.#render();
    }

    disconnectedCallback() {
      this.dataset.cpsbReady = 'false';
      this.#listeners.forEach(([target, type, handler, capture]) =>
        target.removeEventListener(type, handler, capture)
      );
      this.#listeners = [];
    }

    #listeners = [];

    /** Tracks the last computed validity so the submit guard stays cheap. */
    #lastValid = false;

    /** Whether the opening photograph has already been dismissed. */
    #uncovered = false;

    #on(target, type, handler, capture = false) {
      if (!target) return;
      target.addEventListener(type, handler, capture);
      this.#listeners.push([target, type, handler, capture]);
    }

    #collectRefs() {
      const q = (name) => this.querySelector(`[data-cpsb="${name}"]`);
      const qa = (name) => Array.from(this.querySelectorAll(`[data-cpsb="${name}"]`));

      this.refs = {
        // preview — indexed by colour block position so a colour block with no
        // image cannot shift the array out of step with the radios
        bases: (() => {
          const map = [];
          this.querySelectorAll('[data-cpsb-colour-index]').forEach((img) => {
            map[Number(img.dataset.cpsbColourIndex)] = img;
          });
          return map;
        })(),
        name: q('preview-name'),
        artLeft: q('preview-art-left'),
        artRight: q('preview-art-right'),
        artBottom: q('preview-art-bottom'),
        configLabel: q('preview-config'),
        frame: q('preview-frame'),

        // inputs
        variantId: this.querySelector('[data-cpsb="variant-id"]'),
        quantity: q('quantity'),
        nameInput: q('name-input'),
        counter: q('name-counter'),
        hint: q('name-hint'),
        error: q('name-error'),

        // radios
        colourInputs: qa('colour-input'),
        sizeInputs: qa('size-input'),
        fontInputs: qa('font-input'),
        designInputs: qa('design-input'),
        inkInputs: qa('ink-input'),
        tierInputs: qa('tier-input'),

        // swatch strip buttons
        swatches: qa('swatch'),

        // labels / readouts
        colourLabels: qa('colour-label'),
        sizeLabels: qa('size-label'),
        inkLabel: q('ink-label'),
        fontLabel: q('font-label'),
        designLabel: q('design-label'),

        stockLeft: q('stock-left'),
        stockPct: q('stock-pct'),
        stockFill: q('stock-fill'),

        priceNow: q('price-now'),
        priceWas: q('price-was'),
        priceSave: q('price-save'),
        installment: q('installment'),

        // The Add to cart button comes from the theme's own snippet, so it is
        // found by the class we handed that snippet rather than a data hook.
        cta: this.querySelector('button.cpsb-cta'),
        ctaLabel:
          this.querySelector('button.cpsb-cta .add-to-cart-text__content span span') ||
          this.querySelector('button.cpsb-cta .add-to-cart-text__content'),
        form: this.querySelector('form[data-type="add-to-cart-form"]'),

        sizePrices: qa('size-price'),
        tierPrices: qa('tier-price'),
        tierSaves: qa('tier-save'),
        tierTags: qa('tier-tag'),

        fontSamples: qa('font-sample'),
      };
    }

    #bind() {
      const { refs } = this;

      refs.colourInputs.forEach((input, index) =>
        this.#on(input, 'change', () => {
          // Matches the design: changing colourway resets the ink override.
          this.state.colour = index;
          this.state.ink = null;
          this.#render();
        })
      );

      this.#warmColours();

      refs.swatches.forEach((button, index) =>
        this.#on(button, 'click', (event) => {
          event.preventDefault();
          // Setting .checked in code fires neither change nor input, so this
          // one has to say so itself.
          this.#uncover();
          this.state.colour = index;
          this.state.ink = null;
          const target = refs.colourInputs[index];
          if (target) target.checked = true;
          this.#render();
        })
      );

      refs.sizeInputs.forEach((input, index) =>
        this.#on(input, 'change', () => {
          this.state.size = index;
          this.#render();
        })
      );

      refs.fontInputs.forEach((input, index) =>
        this.#on(input, 'change', () => {
          this.state.font = index;
          this.#render();
        })
      );

      refs.designInputs.forEach((input, index) =>
        this.#on(input, 'change', () => {
          this.state.design = index;
          this.#render();
        })
      );

      refs.inkInputs.forEach((input, index) =>
        this.#on(input, 'change', () => {
          this.state.ink = index;
          this.#render();
        })
      );

      refs.tierInputs.forEach((input, index) =>
        this.#on(input, 'change', () => {
          this.state.tier = index;
          this.#render();
        })
      );

      if (refs.nameInput) {
        this.#on(refs.nameInput, 'input', () => {
          this.state.name = this.#sanitize(refs.nameInput.value);
          if (refs.nameInput.value !== this.state.name) {
            refs.nameInput.value = this.state.name;
          }
          this.#render();
        });
      }

      // Belt and braces on validation. The disabled attribute is the visible
      // affordance, but the theme's own form code also manages that button, so
      // block the submit in the capture phase rather than trusting `disabled`.
      const guard = (event) => {
        if (this.#lastValid) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const input = refs.nameInput;
        if (input) {
          /* Focus first without moving anything, then scroll once. Letting the
             focus do its own scrolling jumps the page and the smooth scroll
             then runs from wherever it landed. */
          input.focus({ preventScroll: true });
          input.scrollIntoView({ block: 'center', behavior: 'smooth' });
          this.#nudge(input);
        }
      };
      this.#on(refs.cta, 'click', guard, true);
      this.#on(refs.form, 'submit', guard, true);

      /* The opening photograph steps aside as soon as the shopper starts
         configuring - any colour, size, font, design or tier radio, the name
         field, a photo upload. Both events bubble, so one pair of listeners on
         the host covers every control at once and a control added later cannot
         forget to do it. */
      this.#on(this, 'change', () => this.#uncover());
      this.#on(this, 'input', () => this.#uncover());

      // Theme editor: reveal and select the block the merchant clicked.
      this.#on(document, 'shopify:block:select', (event) => {
        const id = event.detail?.blockId;
        if (!id) return;
        const target = this.querySelector(`[data-cpsb-block-id="${id}"]`);
        if (!target) return;
        const input = target.matches('input')
          ? target
          : target.querySelector('input');
        if (input && !input.disabled) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }

    /**
     * Hand the stage over from the opening photograph to the live preview.
     *
     * Every cover in the document, not only this section's: the per-design
     * scripts clone the whole preview frame into the mobile dock, and the clone
     * carries its own <img> that would otherwise keep covering the mirror. The
     * clone's own attribute sync copies `hidden` across either way, so doing it
     * here is belt and braces rather than the only mechanism.
     */
    #uncover() {
      if (this.#uncovered) return;
      this.#uncovered = true;
      document.querySelectorAll('.cpsb-preview__cover').forEach((cover) => {
        cover.hidden = true;
      });
    }

    /**
     * Every colourway bar the first renders `hidden loading="lazy"`, which the
     * browser will not fetch until the moment it is shown — so the first look
     * at each colour is a cold download. Warm the picture behind a swatch as
     * soon as a finger or a cursor lands on it; the shared warmer sweeps the
     * rest once the page is idle.
     */
    #warmColours() {
      const warmer = window.cpWarm;
      if (!warmer) return;
      const { refs } = this;

      const targets = [];
      refs.colourInputs.forEach((input, index) => {
        const base = refs.bases[index];
        if (!base) return;
        const label = input.id ? this.querySelector(`label[for="${input.id}"]`) : null;
        targets.push([label || refs.swatches[index], base]);
      });

      targets.forEach(([host, base]) => {
        if (!host) return;
        const warm = () => warmer.element(base);
        this.#on(host, 'pointerenter', warm);
        this.#on(host, 'touchstart', warm);
        this.#on(host, 'focusin', warm);
      });
    }

    /* --------------------------------------------------------- name rules */

    #sanitize(value) {
      const { allowedPattern, maxLength } = this.config.validation;
      let out = String(value || '').normalize('NFC');
      if (allowedPattern) {
        try {
          /* Accented letters come first: the setting usually ends in a hyphen,
             which would otherwise start a range. */
          out = out.replace(new RegExp(`[^À-ÖØ-öø-ſ${allowedPattern}]`, 'g'), '');
        } catch {
          /* a bad pattern from the editor must not break typing */
        }
      }
      return out.slice(0, maxLength);
    }

    /* -------------------------------------------------------- computation */

    #derive() {
      const c = this.config;
      const s = this.state;

      const colour = c.colours[s.colour] || c.colours[0] || {};
      const size = c.sizes[s.size] || c.sizes[0] || {};
      const font = c.fonts[s.font] || c.fonts[0] || {};
      const design = c.designs[s.design] || c.designs[0] || {};
      const inkIndex = s.ink == null ? colour.defaultInk ?? 0 : s.ink;
      const ink = c.inks[inkIndex] || c.inks[0] || {};
      const tier = c.tiers[s.tier] || c.tiers[0] || { quantity: 1, discount: 0 };

      const trimmed = s.name.trim();
      const shown = trimmed || c.copy.namePlaceholder;
      const text = design.caps || font.caps ? shown.toUpperCase() : shown;

      // Autofit, ported from the design: clamp the per-character width first,
      // then apply the font's own optical multiplier.
      const length = Math.max(text.length, 4);
      const sideCount = (design.left ? 1 : 0) + (design.right ? 1 : 0);
      const divisor = sideCount ? c.preview.divisorWithArt : c.preview.divisorPlain;
      const cqw =
        clamp(c.preview.minWidth, divisor / length, c.preview.maxWidth) *
        (font.sizeFactor || 1);

      const variant = this.#findVariant(colour.value, size.value);
      const unit = variant ? variant.price : 0;

      /* The was-price is what the order would have cost at full price, which
         is the compare-at when the variant carries one and the ordinary price
         when it does not. Before this it was only ever the price itself, so
         the row could only report a bundle discount — a single bowl with a
         compare-at set in admin showed no saving at all.

         The two stack rather than replace each other: gross is the compare-at
         across the quantity, net is what the button actually charges, and the
         difference is everything the shopper saves from either source. */
      const full = variant && variant.compareAt > unit ? variant.compareAt : unit;
      const gross = full * tier.quantity;
      const net = Math.max(0, unit * tier.quantity - tierOff(unit, tier));
      const saved = Math.max(0, gross - net);

      return {
        colour,
        size,
        font,
        design,
        ink,
        inkIndex,
        tier,
        trimmed,
        text,
        cqw,
        variant,
        unit,
        gross,
        net,
        saved,
        savePct: gross > net ? Math.round((saved / gross) * 100) : 0,
        valid: trimmed.length >= (c.validation.minLength || 1),
      };
    }

    #findVariant(colourValue, sizeValue) {
      return (
        this.config.variants.find(
          (v) => v.option1 === colourValue && v.option2 === sizeValue
        ) || null
      );
    }

    /* ------------------------------------------------------------- render */

    #render() {
      let d;
      try {
        d = this.#derive();
      } catch (error) {
        console.warn('[cpsb] derive failed', error);
        this.#failSafe();
        return;
      }

      try {
        this.#renderPreview(d);
      } catch (error) {
        // A broken preview must never block the sale.
        console.warn('[cpsb] preview render failed', error);
      }

      try {
        this.#renderForm(d);
        this.#renderPrices(d);
        this.#renderValidation(d);
        this.#renderFontSamples(d);
        this.#broadcast(d);
      } catch (error) {
        console.warn('[cpsb] form render failed', error);
        this.#failSafe();
      }
    }

    /** Leave the form usable no matter what went wrong above. */
    #failSafe() {
      const cta = this.refs?.cta;
      if (cta && cta.dataset.cpsbSoldOut !== 'true') cta.disabled = false;
    }

    #renderPreview(d) {
      const { refs, config } = this;
      const style = refs.frame ? refs.frame.style : null;

      refs.bases.forEach((img, index) => {
        const active = index === this.state.colour;
        if (active) {
          img.removeAttribute('hidden');
          img.setAttribute('loading', 'eager');
        } else {
          img.setAttribute('hidden', '');
        }
      });

      if (style) {
        style.setProperty('--cpsb-name-size', `${d.cqw.toFixed(2)}cqw`);
        style.setProperty('--cpsb-name-font', d.font.family || '');
        style.setProperty('--cpsb-name-weight', String(d.font.weight || 700));
        style.setProperty('--cpsb-ink-colour', d.ink.hex || '#f7f7f5');
        style.setProperty(
          '--cpsb-ink-shadow',
          d.ink.light
            ? '0 1px 2px rgba(0,0,0,.30)'
            : '0 1px 0 rgba(255,255,255,.45)'
        );
        style.setProperty(
          '--cpsb-art-gap',
          `calc(${d.cqw.toFixed(2)}cqw * ${config.preview.artGapRatio})`
        );
        style.setProperty('--cpsb-motif-scale', String(d.design.scale || 1));
        // Cut-out details (the dog's eyes and mouth) flip with the print colour
        // so they never vanish into it.
        style.setProperty('--cpsb-motif-detail', d.ink.detail || '#ffffff');
        style.setProperty('--cpsb-art-left-offset', `${d.design.leftOffset || 0}em`);
        style.setProperty('--cpsb-art-right-offset', `${d.design.rightOffset || 0}em`);
      }

      if (refs.name) refs.name.textContent = d.text;

      if (d.design.image) {
        // An uploaded motif replaces the whole icon set, so it is drawn once.
        this.#paintImage(refs.artLeft, d.design.image);
        this.#paintArt(refs.artRight, null, d);
        this.#paintArt(refs.artBottom, null, d);
      } else {
        this.#paintArt(refs.artLeft, d.design.left, d);
        this.#paintArt(
          refs.artRight,
          d.design.raised || d.design.right,
          d,
          Boolean(d.design.raised)
        );
        this.#paintArt(refs.artBottom, d.design.bottom, d);
      }

      if (refs.configLabel) {
        refs.configLabel.textContent = interpolate(config.copy.configLabel, {
          colour: d.colour.label,
          size: d.size.label,
          font: d.font.label,
          design: d.design.label,
        });
      }
    }

    /**
     * Paint one motif slot. Prefers the design block's uploaded artwork, then
     * falls back to the inline SVG template for the chosen motif key.
     */
    /** Draw an uploaded motif image into a slot. */
    #paintImage(slot, src) {
      if (!slot) return;
      slot.textContent = '';
      slot.classList.remove('cpsb-preview__art--raised');
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      slot.appendChild(img);
    }

    #paintArt(slot, motif, d, raised) {
      if (!slot) return;
      slot.textContent = '';
      slot.classList.toggle('cpsb-preview__art--raised', Boolean(raised));
      if (!motif) return;

      // Raised motifs have their own smaller templates, matching the design.
      const template =
        (raised && this.querySelector(`[data-cpsb-motif-raised="${motif}"]`)) ||
        this.querySelector(`[data-cpsb-motif="${motif}"]`);
      if (template && 'content' in template) {
        slot.appendChild(template.content.cloneNode(true));
      }
    }

    #renderForm(d) {
      const { refs } = this;

      if (refs.variantId && d.variant) refs.variantId.value = String(d.variant.id);
      if (refs.quantity) refs.quantity.value = String(d.tier.quantity || 1);

      // Keep the ink radio in sync with the colourway default.
      if (this.state.ink == null) {
        const target = refs.inkInputs[d.inkIndex];
        if (target) target.checked = true;
      }

      const setText = (ref, value) => {
        if (ref) ref.textContent = value;
      };

      refs.colourLabels.forEach((node) => setText(node, d.colour.label));
      refs.sizeLabels.forEach((node) => setText(node, d.size.label));
      setText(refs.inkLabel, d.ink.label);
      setText(refs.fontLabel, d.font.label);
      setText(refs.designLabel, d.design.label);

      // Swatch strip current state
      refs.swatches.forEach((button, index) =>
        button.setAttribute('aria-current', index === this.state.colour ? 'true' : 'false')
      );

      // Stock meter
      const batch = this.config.stock.batchSize || 60;
      const left = Number(d.colour.stock) || 0;
      const pct = clamp(0, Math.round(((batch - left) / batch) * 100), 100);
      setText(
        refs.stockLeft,
        interpolate(this.config.copy.stockLeft, { count: left, colour: d.colour.label })
      );
      setText(refs.stockPct, interpolate(this.config.copy.stockClaimed, { percent: pct }));
      if (refs.stockFill) refs.stockFill.style.width = `${pct}%`;

      // Availability on each size card
      refs.sizeInputs.forEach((input, index) => {
        const size = this.config.sizes[index];
        if (!size) return;
        const variant = this.#findVariant(d.colour.value, size.value);
        const unavailable = !variant || !variant.available;
        input.disabled = unavailable && this.config.stock.hideUnavailable;
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('cpsb-size--unavailable', unavailable);
      });

      // Availability on each colour chip
      refs.colourInputs.forEach((input, index) => {
        const colour = this.config.colours[index];
        if (!colour) return;
        const anyAvailable = this.config.sizes.some((size) => {
          const variant = this.#findVariant(colour.value, size.value);
          return variant && variant.available;
        });
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('cpsb-chip--unavailable', !anyAvailable);
      });
    }

    #renderPrices(d) {
      const { refs, config } = this;
      const money = (cents) => formatMoney(cents, config.moneyFormat);

      if (refs.priceNow) refs.priceNow.textContent = money(d.net);

      if (refs.priceWas) {
        refs.priceWas.textContent = money(d.gross);
        refs.priceWas.hidden = d.savePct <= 0;
      }
      if (refs.priceSave) {
        refs.priceSave.textContent = interpolate(config.copy.savings, {
          amount: money(d.saved),
          percent: d.savePct,
        });
        refs.priceSave.hidden = d.savePct <= 0;
      }
      if (refs.installment) {
        refs.installment.textContent = interpolate(config.copy.installment, {
          amount: money(Math.round(d.net / 4)),
        });
      }

      // Per-size price labels track the real variant prices for this colour.
      refs.sizePrices.forEach((node, index) => {
        const size = config.sizes[index];
        if (!size) return;
        const variant = this.#findVariant(d.colour.value, size.value);
        node.textContent = variant ? money(variant.price) : '';
      });

      // Bundle tier maths
      refs.tierPrices.forEach((node, index) => {
        const tier = config.tiers[index];
        if (!tier) return;
        const gross = d.unit * tier.quantity;
        const net = Math.max(0, gross - tierOff(d.unit, tier));
        node.textContent = money(net);

        const saveNode = refs.tierSaves[index];
        if (saveNode) {
          saveNode.textContent =
            gross > net ? interpolate(config.copy.tierSave, { amount: money(gross - net) }) : '';
        }
        const tagNode = refs.tierTags[index];
        if (tagNode && tagNode.dataset.cpsbTagTemplate) {
          const pct = gross > net ? Math.round(((gross - net) / gross) * 100) : 0;
          tagNode.textContent = interpolate(tagNode.dataset.cpsbTagTemplate, { percent: pct });
        }
      });
    }

    #renderValidation(d) {
      const { refs, config } = this;
      const used = this.state.name.length;

      if (refs.counter) {
        refs.counter.textContent = interpolate(config.copy.counter, {
          used,
          max: config.validation.maxLength,
        });
      }

      if (refs.hint) {
        refs.hint.textContent = d.valid
          ? config.copy.hintValid
          : config.copy.hintEmpty;
      }

      const soldOut = !d.variant || !d.variant.available;
      const blocked = !d.valid || soldOut;
      this.#lastValid = !blocked;

      if (refs.nameInput) {
        refs.nameInput.dataset.cpsbValid = d.valid ? 'true' : 'false';
        refs.nameInput.setAttribute('aria-invalid', d.valid ? 'false' : 'true');
      }

      if (refs.error) {
        refs.error.textContent = soldOut
          ? config.copy.soldOut
          : d.valid
            ? ''
            : config.copy.nameRequired;
      }

      /* A disabled button fires no click, so the guard below — which exists to
         take the shopper to the field they have not filled in — could never
         run: the button simply greyed out and nothing explained why. Where the
         section supplies the copy for it, the button stays live and carries
         aria-disabled instead, which reads the same to assistive technology
         and still looks unavailable, but can be clicked and answered.

         Sold out is different and stays truly disabled: there is nothing the
         shopper can do about it, so there is nothing to send them to. */
      const askForName = Boolean(config.copy.ctaEmpty) && !soldOut && !d.valid;

      if (refs.cta) {
        refs.cta.dataset.cpsbSoldOut = soldOut ? 'true' : 'false';
        refs.cta.disabled = blocked && !askForName;
        if (askForName) {
          refs.cta.setAttribute('aria-disabled', 'true');
        } else {
          refs.cta.removeAttribute('aria-disabled');
        }
      }
      if (refs.ctaLabel) {
        refs.ctaLabel.textContent = soldOut
          ? config.copy.ctaSoldOut
          : askForName
            ? config.copy.ctaEmpty
            : interpolate(config.copy.cta, { price: formatMoney(d.net, config.moneyFormat) });
      }
    }

    /**
     * Draw the eye to the field the shopper has to fill in.
     *
     * Scrolling it into view is not enough on its own: on a long buy form the
     * field arrives looking like every other row, and a focus ring alone is
     * easy to miss on a phone. A short movement says "here" in a way a colour
     * change does not — and it is movement for its own sake, so anyone who has
     * asked their system for less of it gets the scroll and the focus without
     * the shake.
     */
    #nudge(node) {
      if (!node) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      node.classList.remove('cpsb-nudge');
      /* Restart the animation even when it is already running: reading back a
         layout property flushes the class removal before it is added again. */
      void node.offsetWidth;
      node.classList.add('cpsb-nudge');
      window.setTimeout(() => node.classList.remove('cpsb-nudge'), 700);
    }

    /** The font swatches preview the customer's actual name, as in the design. */
    #renderFontSamples(d) {
      const sample = this.state.name.trim() || this.config.copy.fontSampleFallback;
      this.refs.fontSamples.forEach((node, index) => {
        const font = this.config.fonts[index];
        if (!font) return;
        if (node.dataset.cpsbHasSwatch === 'true') return;
        node.textContent = font.caps ? sample.toUpperCase() : sample;
        const size = Math.max(
          11,
          Math.min(21, 74 / Math.max(sample.length, 3)) * (font.sizeFactor || 1)
        );
        node.style.setProperty('--cpsb-sample-size', `${size.toFixed(0)}px`);
      });
    }

    #broadcast(d) {
      document.dispatchEvent(
        new CustomEvent(STATE_EVENT, {
          bubbles: true,
          detail: {
            sectionId: this.dataset.cpsbSection,
            name: d.trimmed,
            colour: d.colour.label,
            size: d.size.label,
            tierTitle: d.tier.title,
            image: (this.refs.bases[this.state.colour] || {}).currentSrc || '',
            priceNow: formatMoney(d.net, this.config.moneyFormat),
            priceWas: formatMoney(d.gross, this.config.moneyFormat),
            hasSavings: d.savePct > 0,
            valid: d.valid,
            formId: this.dataset.cpsbForm || '',
          },
        })
      );
    }
  }

  /* =========================================================================
     <cpsb-sticky> — mirrors the buy state in the fixed bottom bar
     ========================================================================= */

  class CpsbSticky extends HTMLElement {
    connectedCallback() {
      this.config = readConfig(this) || {};
      this.refs = {
        title: this.querySelector('[data-cpsb="sticky-title"]'),
        thumb: this.querySelector('[data-cpsb="sticky-thumb"]'),
        priceNow: this.querySelector('[data-cpsb="sticky-price"]'),
        priceWas: this.querySelector('[data-cpsb="sticky-was"]'),
        cta: this.querySelector('[data-cpsb="sticky-cta"]'),
      };

      this.onState = (event) => this.#apply(event.detail);
      document.addEventListener(STATE_EVENT, this.onState);

      if (this.refs.cta) {
        this.onClick = (event) => {
          // Submit the real product form so the theme's AJAX cart handles it.
          const formId = this.refs.cta.dataset.cpsbForm;
          const form = formId ? document.getElementById(formId) : null;
          const submit = form?.querySelector('[name="add"]');
          if (submit && !submit.disabled) {
            event.preventDefault();
            submit.click();
            return;
          }
          // Nothing to submit (invalid name or no JS state yet) — send the
          // shopper to the buy box instead of failing silently.
          event.preventDefault();
          const target = document.querySelector('[data-cpsb="name-input"]') || form;
          target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (target && 'focus' in target) target.focus({ preventScroll: true });
        };
        this.refs.cta.addEventListener('click', this.onClick);
      }
    }

    disconnectedCallback() {
      if (this.onState) document.removeEventListener(STATE_EVENT, this.onState);
      if (this.onClick && this.refs?.cta) {
        this.refs.cta.removeEventListener('click', this.onClick);
      }
    }

    #apply(detail) {
      if (!detail) return;
      const { refs } = this;

      if (refs.title) {
        refs.title.textContent = interpolate(this.config.titleTemplate, {
          name: detail.name || this.config.namePlaceholder || '',
          colour: detail.colour,
          size: detail.size,
          tier: detail.tierTitle,
        });
      }
      if (refs.thumb && detail.image) refs.thumb.src = detail.image;
      if (refs.priceNow) refs.priceNow.textContent = detail.priceNow;
      if (refs.priceWas) {
        refs.priceWas.textContent = detail.priceWas;
        refs.priceWas.hidden = !detail.hasSavings;
      }
      if (refs.cta && detail.formId) refs.cta.dataset.cpsbForm = detail.formId;
    }
  }

  /* =========================================================================
     <cpsb-toast> — rotating social proof
     ========================================================================= */

  class CpsbToast extends HTMLElement {
    connectedCallback() {
      this.config = readConfig(this) || {};
      this.items = Array.from(this.querySelectorAll('[data-cpsb="toast-item"]'));
      if (!this.items.length) return;

      this.index = -1;
      const first = Math.max(1000, Number(this.config.firstDelay) || 8000);
      const every = Math.max(2000, Number(this.config.interval) || 8000);

      this.startTimer = window.setTimeout(() => {
        this.#advance();
        this.loopTimer = window.setInterval(() => this.#advance(), every);
      }, first);
    }

    disconnectedCallback() {
      window.clearTimeout(this.startTimer);
      window.clearInterval(this.loopTimer);
    }

    #advance() {
      this.index = (this.index + 1) % this.items.length;
      this.items.forEach((item, i) => {
        item.hidden = i !== this.index;
      });
      this.removeAttribute('hidden');
    }
  }

  /* =========================================================================
     <cpsb-scroll-cta> — closing CTA that focuses the buy box
     ========================================================================= */

  class CpsbScrollCta extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-cpsb="scroll-cta"]');
      if (!this.button) return;
      this.onClick = (event) => {
        const target = document.querySelector('[data-cpsb="name-input"]');
        if (!target) return; // let the default anchor behaviour run
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.focus({ preventScroll: true });
      };
      this.button.addEventListener('click', this.onClick);

      this.onState = (event) => {
        const name = event.detail?.name;
        const head = this.querySelector('[data-cpsb="closing-head"]');
        if (!head || !this.dataset.cpsbHeadNamed || !this.dataset.cpsbHeadPlain) return;
        head.textContent = name
          ? interpolate(this.dataset.cpsbHeadNamed, { name: titleCase(name) })
          : this.dataset.cpsbHeadPlain;
      };
      document.addEventListener(STATE_EVENT, this.onState);
    }

    disconnectedCallback() {
      if (this.onClick && this.button) this.button.removeEventListener('click', this.onClick);
      if (this.onState) document.removeEventListener(STATE_EVENT, this.onState);
    }
  }

  /* =========================================================================
     <cpsb-countdown> — urgency bar timer
     ========================================================================= */

  class CpsbCountdown extends HTMLElement {
    connectedCallback() {
      this.config = readConfig(this) || {};
      this.output = this.querySelector('[data-cpsb="countdown-text"]');
      if (!this.output || this.config.mode !== 'countdown') return;

      this.#tick();
      this.timer = window.setInterval(() => this.#tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    #tick() {
      const cutoffHour = Number(this.config.cutoffHour);
      const now = new Date();
      const cutoff = new Date(now);
      cutoff.setHours(Number.isFinite(cutoffHour) ? cutoffHour : 15, 0, 0, 0);
      if (cutoff < now) cutoff.setDate(cutoff.getDate() + 1);

      const total = Math.max(0, Math.floor((cutoff - now) / 1000));
      const pad = (n) => String(n).padStart(2, '0');
      const clock = `${pad(Math.floor(total / 3600))}:${pad(
        Math.floor(total / 60) % 60
      )}:${pad(total % 60)}`;

      this.output.textContent = interpolate(this.config.template, { time: clock });
    }
  }

  /* ------------------------------------------------------------- register */

  /* =========================================================================
     <cpsb-lightbox> — centred image viewer for the gallery thumbnails

     Built on the native <dialog> element, so focus trapping, Escape-to-close
     and returning focus to the thumbnail that opened it all come from the
     platform rather than hand-rolled JS.
     ========================================================================= */

  class CpsbLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-cpsb="lightbox"]');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.img = this.querySelector('[data-cpsb="lightbox-img"]');
      this.counter = this.querySelector('[data-cpsb="lightbox-count"]');

      // Thumbnails live in the same section, outside this element.
      const scope = this.closest('.cpsb-hero') || document;
      this.thumbs = Array.from(scope.querySelectorAll('[data-cpsb="thumb"]'));
      /* The bowl itself opens the viewer too, on the first image. It is not one
         of the gallery photographs — it is a live render of the customer's own
         bowl — so there is no index of its own to open at, and the gallery
         reads from the start. */
      this.opener = scope.querySelector('[data-cpsb="preview-open"]');
      if (!this.thumbs.length) return;

      this.index = 0;
      this.#bind();
    }

    disconnectedCallback() {
      this.#listeners.forEach(([t, type, fn, options]) => t.removeEventListener(type, fn, options));
      this.#listeners = [];
      if (this.dialog?.open) this.dialog.close();
    }

    #listeners = [];

    #on(target, type, fn, options) {
      if (!target) return;
      target.addEventListener(type, fn, options);
      this.#listeners.push([target, type, fn, options]);
    }

    #bind() {
      this.thumbs.forEach((thumb, i) =>
        this.#on(thumb, 'click', (event) => {
          event.preventDefault();
          this.#open(i);
        })
      );

      this.#on(this.opener, 'click', (event) => {
        event.preventDefault();
        this.#open(0);
      });

      this.#on(this.querySelector('[data-cpsb="lightbox-close"]'), 'click', () =>
        this.dialog.close()
      );
      this.#on(this.querySelector('[data-cpsb="lightbox-prev"]'), 'click', () =>
        this.#step(-1)
      );
      this.#on(this.querySelector('[data-cpsb="lightbox-next"]'), 'click', () =>
        this.#step(1)
      );

      // Clicking the backdrop resolves to the dialog element itself.
      this.#on(this.dialog, 'click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.#on(this.dialog, 'keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.#step(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.#step(-1); }
      });

      this.#bindSwipe();
    }

    /**
     * Swipe between images on a touch screen.
     *
     * The arrows and the arrow keys were the only way through the gallery,
     * and on a phone that is a pair of small targets over a full-screen photo
     * — every other full-screen image viewer a customer has used moves on a
     * swipe. The stage behind the lightbox cannot do this job: on these pages
     * it is a live preview of the shopper's own configuration, not a
     * photograph, so it has nothing to page through.
     *
     * Listeners go on the dialog rather than the <img> so the gesture is
     * caught anywhere on the screen, including the letterboxed margins beside
     * a portrait photo.
     *
     * Deliberately not a scroll-snap track: the image src is swapped in place
     * by #show, so there is only ever one slide in the DOM to scroll.
     */
    #bindSwipe() {
      if (!this.dialog) return;

      /* Below this a swipe is indistinguishable from a tap that moved, and
         the ratio keeps a vertical drag from paging the gallery sideways. */
      const MIN_X = 45;
      let x0 = null;
      let y0 = null;

      this.#on(this.dialog, 'touchstart', (event) => {
        if (event.touches.length !== 1) return;
        x0 = event.touches[0].clientX;
        y0 = event.touches[0].clientY;
      }, { passive: true });

      this.#on(this.dialog, 'touchend', (event) => {
        if (x0 === null) return;
        const touch = event.changedTouches && event.changedTouches[0];
        const dx = touch ? touch.clientX - x0 : 0;
        const dy = touch ? touch.clientY - y0 : 0;
        x0 = null;
        y0 = null;
        if (Math.abs(dx) < MIN_X || Math.abs(dx) <= Math.abs(dy)) return;
        this.#step(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    #open(index) {
      this.#show(index);
      if (!this.dialog.open) this.dialog.showModal();
    }

    #step(delta) {
      this.#show((this.index + delta + this.thumbs.length) % this.thumbs.length);
    }

    #show(index) {
      const thumb = this.thumbs[index];
      if (!thumb || !this.img) return;
      this.index = index;
      // Set the intrinsic size before the source so the browser reserves the
      // right aspect ratio instead of the placeholder square in the markup.
      const w = Number(thumb.dataset.cpsbW);
      const h = Number(thumb.dataset.cpsbH);
      if (w > 0 && h > 0) {
        this.img.width = w;
        this.img.height = h;
      }
      this.img.src = thumb.dataset.cpsbFull || '';
      this.img.alt = thumb.dataset.cpsbAlt || '';
      if (this.counter) {
        this.counter.textContent = `${index + 1} / ${this.thumbs.length}`;
      }
    }
  }

  /* =========================================================================
     <cpsb-rail> — horizontal scroller for the product media thumbnails
     ========================================================================= */

  class CpsbRail extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-cpsb="rail"]');
      this.prev = this.querySelector('[data-cpsb="rail-prev"]');
      this.next = this.querySelector('[data-cpsb="rail-next"]');
      if (!this.rail) return;

      this.onPrev = () => this.#page(-1);
      this.onNext = () => this.#page(1);
      this.onScroll = () => this.#sync();

      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.#sync());
        this.observer.observe(this.rail);
      }
      this.#sync();
    }

    disconnectedCallback() {
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.rail?.removeEventListener('scroll', this.onScroll);
      this.observer?.disconnect();
    }

    /** Scroll by one visible page, leaving a tile of overlap for context. */
    #page(direction) {
      const step = Math.max(80, this.rail.clientWidth * 0.8);
      this.rail.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    #sync() {
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      const overflowing = max > 4;
      const x = this.rail.scrollLeft;
      if (this.prev) this.prev.hidden = !overflowing || x <= 4;
      if (this.next) this.next.hidden = !overflowing || x >= max - 4;
    }
  }

  /* =========================================================================
     Lead price — the copy shown under the subheading

     A mirror of the price at the foot of the buy form, never a second
     calculation. Bundle tiers, per-size variant prices and savings are all
     worked out in one place; this only repeats the answer higher up the page
     where a phone shopper can see it before the configurator.

     It copies the node rather than subscribing to the cpsb:state broadcast.
     The broadcast only fires on a change, and the price is recomputed once
     during init — a default bundle tier is not the bare variant price — so a
     subscriber would sit on the server-rendered figure until the shopper
     touched a control, showing one price at the top of the page and another at
     the bottom. The initial copy covers the case where that init has already
     run; the observer covers it if it runs afterwards.

     Deliberately independent of the personalizer element, so it works the same
     for the heroes that swap in their own controller.
     ====================================================================== */
  function wireLeadPrice(box) {
    if (box.dataset.cpsbLeadReady === 'on') return;

    const hero = box.closest('.cpsb-hero') || document;
    const sourceNow = hero.querySelector('[data-cpsb="price-now"]');
    if (!sourceNow) return;

    box.dataset.cpsbLeadReady = 'on';

    const sourceWas = hero.querySelector('[data-cpsb="price-was"]');
    const sourceSave = hero.querySelector('[data-cpsb="price-save"]');
    const now = box.querySelector('[data-cpsb="leadprice-now"]');
    const was = box.querySelector('[data-cpsb="leadprice-was"]');
    const save = box.querySelector('[data-cpsb="leadprice-save"]');

    const copy = () => {
      if (now) now.textContent = sourceNow.textContent;
      if (was && sourceWas) {
        was.textContent = sourceWas.textContent;
        was.hidden = sourceWas.hidden;
      }
      /* The saving carries its own hidden flag: a colourway with no compare-at
         on the single-bowl tier has none to state, and the pill has to go
         rather than sit there empty. */
      if (save && sourceSave) {
        save.textContent = sourceSave.textContent;
        save.hidden = sourceSave.hidden;
      }
    };

    copy();

    if (!window.MutationObserver) return;
    const observer = new MutationObserver(copy);
    const watch = { childList: true, characterData: true, subtree: true };
    observer.observe(sourceNow, watch);
    if (sourceSave) {
      observer.observe(sourceSave, {
        ...watch,
        attributes: true,
        attributeFilter: ['hidden'],
      });
    }
    if (sourceWas) {
      observer.observe(sourceWas, {
        ...watch,
        attributes: true,
        attributeFilter: ['hidden'],
      });
    }
  }

  const bootLeadPrices = () => {
    document.querySelectorAll('[data-cpsb="leadprice"]').forEach(wireLeadPrice);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootLeadPrices, { once: true });
  } else {
    bootLeadPrices();
  }
  document.addEventListener('shopify:section:load', bootLeadPrices);

  const define = (tag, ctor) => {
    if (!window.customElements.get(tag)) window.customElements.define(tag, ctor);
  };

  define('cpsb-personalizer', CpsbPersonalizer);
  define('cpsb-lightbox', CpsbLightbox);
  define('cpsb-rail', CpsbRail);
  define('cpsb-sticky', CpsbSticky);
  define('cpsb-toast', CpsbToast);
  define('cpsb-scroll-cta', CpsbScrollCta);
  define('cpsb-countdown', CpsbCountdown);
})();
